import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/site/PageShell";
import { SectionLabel } from "@/components/site/Reveal";
import { ZONES } from "@/data/content";
import { ZONE_DETAILS, GALLERY_GROUPS } from "@/data/zoneDetails";

const Lightbox = ({ item, onClose }) => {
  useEffect(() => {
    const on = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [onClose]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-lenis-prevent
      className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-10" data-testid="gallery-lightbox">
      <button data-testid="gallery-lightbox-close" className="absolute top-5 right-5 w-11 h-11 rounded-full bg-[#FFEF00] text-black flex items-center justify-center" aria-label="Close"><X size={20} /></button>
      <motion.figure initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ duration: 0.4 }} onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full">
        <img src={item.img} alt={item.title} className="w-full max-h-[78vh] object-contain rounded-[20px]" />
        <figcaption className="mt-4 flex items-center justify-between gap-4">
          <span className="font-display font-extrabold text-xl">{item.title}</span>
          <Link to={`/zones/${item.key}`} data-testid="gallery-lightbox-zone-link" className="text-sm text-[#14C3F7] hover:text-[#FFEF00]">View zone →</Link>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
};

export default function GalleryPage() {
  const [group, setGroup] = useState("all");
  const [open, setOpen] = useState(null);
  const items = ZONES.filter((z) => group === "all" || ZONE_DETAILS[z.key]?.group === group);
  return (
    <PageShell testId="gallery-page">
      <section className="pt-36 pb-24 max-w-[1400px] mx-auto px-5 md:px-10">
        <SectionLabel>Gallery</SectionLabel>
        <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">Caught<br /><span className="text-[#FFEF00]">in play.</span></h1>
        <div className="mt-10 flex flex-wrap gap-2">
          {GALLERY_GROUPS.map((g) => (
            <button key={g.id} data-testid={`gallery-filter-${g.id}`} onClick={() => setGroup(g.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold border-2 transition-colors ${group === g.id ? "bg-[#FFEF00] text-black border-black" : "border-white/15 hover:border-[#FFEF00]"}`}>{g.label}</button>
          ))}
        </div>
        <motion.div layout className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5">
          {items.map((z, i) => (
            <motion.button layout key={z.key} data-testid={`gallery-item-${z.key}`} onClick={() => setOpen(z)}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
              className="group relative mb-5 block w-full break-inside-avoid overflow-hidden rounded-[22px] border border-white/10 hover:border-[#14C3F7]/70">
              <img src={z.img} alt={z.title} loading="lazy" className={`w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105 ${i % 3 === 1 ? "aspect-[4/5]" : "aspect-[3/2]"}`} />
              <span className="absolute bottom-4 left-4 rounded-full bg-black/60 backdrop-blur-md px-3 py-1.5 font-display font-bold text-sm">{z.title}</span>
            </motion.button>
          ))}
        </motion.div>
      </section>
      <AnimatePresence>{open && <Lightbox item={open} onClose={() => setOpen(null)} />}</AnimatePresence>
    </PageShell>
  );
}
